// Install plumbing shared by the sheet and the banner.
// Chrome (Android + desktop) fires `beforeinstallprompt` once the site is
// installable; it must be captured early, so src/index.js imports this module
// before React renders. iOS has no event: the sheet shows the manual steps.
import { isStandalone } from './platform';

const DISMISS_KEY = 'byopdf:install:dismissed';
const LISTENED_KEY = 'byopdf:install:listened';

let deferredPrompt = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => { try { fn(); } catch (e) { /* listener error */ } });

const track = (name, params) => {
  try { if (typeof window.gtag === 'function') window.gtag('event', name, params || {}); } catch (e) { /* no analytics */ }
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    track('pwa_installed');
    notify();
  });
  if (isStandalone()) track('pwa_launch');
}

export const subscribeInstall = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

export const canPromptInstall = () => Boolean(deferredPrompt);

/** Open Chrome's native install dialog. Resolves 'accepted' | 'dismissed' | 'unavailable'. */
export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';
  const event = deferredPrompt;
  deferredPrompt = null;
  notify();
  track('pwa_install_prompt');
  try {
    event.prompt();
    const choice = await event.userChoice;
    track('pwa_install_choice', { outcome: choice?.outcome });
    return choice?.outcome || 'dismissed';
  } catch (e) {
    return 'dismissed';
  }
}

export const isInstallDismissed = () => {
  try { return Boolean(localStorage.getItem(DISMISS_KEY)); } catch (e) { return false; }
};

export const dismissInstall = (source) => {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) { /* ignore */ }
  track('pwa_install_dismissed', { source });
  notify();
};

export const hasListened = () => {
  try { return Boolean(localStorage.getItem(LISTENED_KEY)); } catch (e) { return false; }
};

export const noteListened = () => {
  try {
    if (!localStorage.getItem(LISTENED_KEY)) {
      localStorage.setItem(LISTENED_KEY, String(Date.now()));
      notify();
    }
  } catch (e) { /* ignore */ }
};

export { track as trackInstall };
